import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';

import { IntegratedFreightShipping } from './IntegratedFreightShippingFragment/IntegratedFreightShippingFragment';
import { NodeType } from '../../../../@types';
import { CourierData } from '../../../../@types/shipment.type';
import { Panel } from '../../../../components/atoms/Panel';
import { SectionLabel } from '../../../../components/atoms/SectionLabel';
import { QuickToggleGroup } from '../../../../components/molecules/QuickToggleGroup';
import {
  ManualShipping,
  ManualShippingData,
} from '../../../../components/organisms/ManualShipping';
import { StructureTypeCategory, NodeAttributeType } from '../../../../configs/enums';
import {
  useGetStructuresByTypeCategoryCodeAndTypeCodeForReplenishment,
  useGetNodeAttributesByNodeId,
} from '../../../../queries/structure-query';

import { ShippingMethodFragmentProps, ShippingMethods } from '.';

export const ShippingMethodFragment: React.FC<ShippingMethodFragmentProps> = ({
  shipment,
  onSaveManual,
}) => {
  const [method, setMethod] = useState<string>(ShippingMethods.Manual);
  const [freightIntegrationEnabled, setFreightIntegrationEnabled] = useState(false);
  const [couriers, setCouriers] = useState<Array<CourierData>>([]);

  const getStructuresByTypeCategoryCodeAndTypeCode =
    useGetStructuresByTypeCategoryCodeAndTypeCodeForReplenishment(
      StructureTypeCategory.Operational,
      NodeType.ShippingCompany
    );
  const getNodeAttributesByNodeId = useGetNodeAttributesByNodeId(shipment.sourceNodeId);

  useEffect(() => {
    if (getStructuresByTypeCategoryCodeAndTypeCode.data) {
      const courierList: Array<CourierData> = getStructuresByTypeCategoryCodeAndTypeCode.data?.map(
        (item) => ({
          id: item.id,
          name: item.name,
        })
      );
      setCouriers(courierList);
    }
  }, [getStructuresByTypeCategoryCodeAndTypeCode.data]);

  useEffect(() => {
    if (getNodeAttributesByNodeId.data) {
      const freightIntegrationEnabled = getNodeAttributesByNodeId.data.some(
        (item) =>
          item.propertyTypeCode === NodeAttributeType.FreightIntegrationEnabled &&
          item.value.freightIntegrationEnabled
      );
      setFreightIntegrationEnabled(freightIntegrationEnabled);
    }
  }, [getNodeAttributesByNodeId.data]);

  const options = useMemo(
    () =>
      (freightIntegrationEnabled ? Object.values(ShippingMethods) : [ShippingMethods.Manual]).map(
        (name) => ({ value: name, label: name })
      ),
    [freightIntegrationEnabled]
  );

  const isManual = method === ShippingMethods.Manual;

  // Handed to whichever body is showing so it sits on the same row as that
  // body's own pickers — method, carrier and service type read as one sequence
  // of choices rather than three stacked blocks.
  const methodToggle =
    options.length > 1 ? (
      <Box>
        <SectionLabel>Method</SectionLabel>
        <QuickToggleGroup
          label="Shipping method"
          options={options}
          value={method}
          onChange={setMethod}
        />
      </Box>
    ) : undefined;

  return (
    <Panel title="Shipping Method">
      {isManual ? (
        <ManualShipping
          couriers={couriers}
          methodToggle={methodToggle}
          onSave={(manualShippingData: ManualShippingData) => onSaveManual?.(manualShippingData)}
        />
      ) : (
        <IntegratedFreightShipping
          shipment={shipment}
          couriers={couriers}
          methodToggle={methodToggle}
        />
      )}
    </Panel>
  );
};
